import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { seoObjectLookup } from 'utils/seo-helper'

import { SiteActivityConfig } from 'events/lib'
import { ISeoMetaData } from 'data/seo'
import { Activity } from '@cms-max/medicare-api-sdk'

export interface PageLoadedContextType {
  loaded: boolean
  eventQueue: SiteActivityConfig[]
  addEvent: (ev: SiteActivityConfig) => void
}

interface PageLoadedProviderProps {
  children: React.ReactNode
  seoMetadata: ISeoMetaData[]
}

const initialPageLoadedState: PageLoadedContextType = {
  loaded: false,
  eventQueue: [],
  addEvent: () => {}
}

export const PageLoadedContext = React.createContext<PageLoadedContextType>(
  initialPageLoadedState
)

export const PageLoadedProvider: React.FC<PageLoadedProviderProps> = ({
  seoMetadata,
  children
}) => {
  const [loaded, setLoaded] = React.useState(false)
  const [eventQueue, setEventQueue] = React.useState<SiteActivityConfig[]>([])

  const location = useLocation()

  const addEvent = (ev: SiteActivityConfig) => {
    setEventQueue((existing) => [...existing, ev])
  }

  const pageLoadedListener = React.useCallback(() => {
    if (!loaded) {
      setLoaded(true)
    }
  }, [loaded])

  React.useEffect(() => {
    if (window.globalHeaderEvents?.handleEvent) {
      setLoaded(true)
    }

    document.addEventListener('cms-header-loaded', pageLoadedListener)

    return () => {
      document.removeEventListener('cms-header-loaded', pageLoadedListener)
    }
  }, [pageLoadedListener])

  // Do page load before handling queued events to make tests make more sense
  React.useEffect(() => {
    if (loaded) {
      const currentPathName = location.pathname.toLowerCase().replace(/\/$/, '')
      const foundSeoObject = seoObjectLookup(seoMetadata, currentPathName)

      // QM MASKING
      if (foundSeoObject?.isQmMaskingEnabled) {
        document.body.classList.add('qm-allow-specific-masking')
      } else {
        document.body.classList.remove('qm-allow-specific-masking')
      }

      window.globalHeaderEvents?.handleEvent({
        core: {
          property: 'MAX',
          activity: Activity.PageLoad,
          component: foundSeoObject?.component ?? 'Medicare My',
          url: window.location.href
        }
      })
    }
  }, [loaded, location, seoMetadata])

  React.useEffect(() => {
    if (loaded && eventQueue.length > 0) {
      eventQueue.forEach((ev) => {
        window.globalHeaderEvents?.handleEvent(ev)
      })
      setEventQueue([])
    }
  }, [loaded, eventQueue])

  return (
    <PageLoadedContext.Provider
      value={{
        loaded,
        eventQueue,
        addEvent
      }}
    >
      {children}
    </PageLoadedContext.Provider>
  )
}

export const usePageLoaded = () => {
  return React.useContext(PageLoadedContext)
}

export default usePageLoaded
